import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Image,
  List,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import type { CatalogueEntry } from "../catalogue/api";
import { CatalogueSearch } from "../catalogue/CatalogueSearch";
import { useCatalogue } from "../catalogue/useCatalogue";
import { type GiveawayAnswer, type ShowState, shows } from "./api";
import styles from "../catalogue/Catalogue.module.scss";

/**
 * The show's giveaway (spec §15.10) — a product given free to buyers, drawn when the show ends.
 *
 * Three faces, by where the giveaway has got to:
 *
 * - **Nothing set** — search the catalogue, pick a product, say how many (`R-GIVE-1`, `R-GIVE-3`).
 * - **Set, not drawn** — what it is and how many, with *Change* and *Remove* until the show ends
 *   (`R-GIVE-8`), and a warning when the catalogue has fewer in stock than that (`R-GIVE-7`).
 * - **Drawn** — who won, or that nobody bought. The record, and nothing to press.
 *
 * ## It reads its own resource and does not follow the page's poll
 *
 * Nothing about a giveaway changes because a customer did something — only the host changes it,
 * here, and the draw changes it once, when the show ends. The page remounts this card on every
 * state change (it is keyed by the show's state), so ending the show is what makes it read the
 * draw's result. Joining the five-second poll would re-read a row that cannot have moved.
 *
 * ## Why stock is a warning
 *
 * The product decision (`R-GIVE-7`). Nothing is taken until each winner's order is created, at the
 * end of the show, so a stock figure read now is a guess at a number that will have moved by then —
 * and refusing on a guess would stop a host adding a giveaway they know will be restocked.
 */
export function GiveawayCard({
  showId,
  showState,
  controls,
}: {
  showId: string;
  showState: ShowState;
  /** False for a moderator: the giveaway is shown, and nothing about it can be changed (spec §3). */
  controls: boolean;
}) {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState<GiveawayAnswer>();
  const [error, setError] = useState<string>();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void shows.giveaway(showId).then(
      (next) => {
        if (live) setAnswer(next);
      },
      (failure: unknown) => {
        if (live)
          setError(
            failure instanceof ApiError ? failure.message : t("shows.unreachable"),
          );
      },
    );
    return () => {
      live = false;
    };
  }, [showId, t]);

  const act = useCallback(
    async (work: () => Promise<GiveawayAnswer>) => {
      setBusy(true);
      setError(undefined);
      try {
        setAnswer(await work());
        setChoosing(false);
      } catch (failure) {
        setError(
          failure instanceof ApiError ? failure.message : t("shows.unreachable"),
        );
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  if (!answer) {
    return error ? (
      <Alert color="red" variant="light" radius="md">
        {error}
      </Alert>
    ) : null;
  }

  const { giveaway, winners, editable } = answer;
  const mayEdit = controls && editable;

  // A moderator on a show with no giveaway has nothing to look at, and an empty card is noise.
  if (!giveaway && !mayEdit) return null;

  const short =
    giveaway?.available !== null &&
    giveaway?.available !== undefined &&
    giveaway.available < giveaway.quantity;

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" fw={600}>
            {t("giveaway.heading")}
          </Text>
          {giveaway ? (
            <Badge
              size="sm"
              variant="light"
              color={giveaway.state === "drawn" ? "green" : "gray"}
            >
              {t(giveaway.state === "drawn" ? "giveaway.drawn" : "giveaway.pending")}
            </Badge>
          ) : null}
        </Group>
        <Text size="xs" c="dimmed">
          {t("giveaway.help")}
        </Text>

        {error ? (
          <Alert
            color="red"
            variant="light"
            radius="md"
            withCloseButton
            onClose={() => setError(undefined)}
          >
            {error}
          </Alert>
        ) : null}

        {giveaway && !choosing ? (
          <Group gap="sm" wrap="nowrap" align="flex-start">
            {giveaway.imageUrl ? (
              <Image
                src={giveaway.imageUrl}
                w={48}
                h={48}
                radius="sm"
                fit="cover"
                alt=""
              />
            ) : null}
            <Stack gap={2} className={styles.grow}>
              <Text size="sm" fw={600} lineClamp={2}>
                {giveaway.productTitle}
                {giveaway.variantTitle ? ` — ${giveaway.variantTitle}` : ""}
              </Text>
              <Text size="xs" c="dimmed">
                {t("giveaway.meta", {
                  sku: giveaway.sku ?? t("catalogue.noSku"),
                  count: giveaway.quantity,
                })}
              </Text>
              {giveaway.state === "pending" ? (
                <Text size="xs" c={short ? "orange.8" : "dimmed"}>
                  {giveaway.available === null
                    ? t("catalogue.untracked")
                    : t("catalogue.available", { count: giveaway.available })}
                </Text>
              ) : null}
            </Stack>
          </Group>
        ) : null}

        {/* `R-GIVE-7` — warned, never refused. */}
        {giveaway && giveaway.state === "pending" && short && !choosing ? (
          <Alert color="orange" variant="light" radius="md">
            {t("giveaway.lowStock", {
              available: giveaway.available,
              quantity: giveaway.quantity,
            })}
          </Alert>
        ) : null}

        {giveaway?.state === "drawn" ? (
          winners.length === 0 ? (
            <Text size="sm" c="dimmed">
              {t("giveaway.noWinners")}
            </Text>
          ) : (
            <Stack gap={4}>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                {t("giveaway.winners", { count: winners.length })}
              </Text>
              <List size="sm" spacing={2}>
                {winners.map((winner) => (
                  <List.Item key={winner.showOrderId}>
                    {winner.customer}
                    {winner.customer !== winner.email ? (
                      <Text span size="xs" c="dimmed">
                        {` · ${winner.email}`}
                      </Text>
                    ) : null}
                  </List.Item>
                ))}
              </List>
              {winners.length < giveaway.quantity ? (
                <Text size="xs" c="dimmed">
                  {t("giveaway.shortExplained", { quantity: giveaway.quantity })}
                </Text>
              ) : null}
            </Stack>
          )
        ) : null}

        {mayEdit && (choosing || !giveaway) ? (
          <GiveawayForm
            key={giveaway?.id ?? "new"}
            initialQuantity={giveaway?.quantity ?? 1}
            quantityMax={answer.quantityMax}
            busy={busy}
            onSave={(variantId, quantity) =>
              act(() => shows.setGiveaway(showId, variantId, quantity))
            }
            onCancel={giveaway ? () => setChoosing(false) : undefined}
          />
        ) : null}

        {mayEdit && giveaway && !choosing ? (
          <Group gap="xs">
            <Button
              size="compact-sm"
              variant="default"
              onClick={() => setChoosing(true)}
            >
              {t("giveaway.change")}
            </Button>
            <Button
              size="compact-sm"
              variant="default"
              color="red"
              loading={busy}
              onClick={() => void act(() => shows.removeGiveaway(showId))}
            >
              {t("giveaway.remove")}
            </Button>
          </Group>
        ) : null}

        {showState === "live" && giveaway?.state === "pending" ? (
          <Text size="xs" c="dimmed">
            {t("giveaway.drawnAtEnd")}
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * What the giveaway search lists by default: anything with a SKU, because the warehouse picks by
 * it. Stock is warned about rather than filtered on (`R-GIVE-7`). Module-level so the search's memo
 * is not recomputed on every render of the page's poll.
 */
function hasSku(entry: CatalogueEntry): boolean {
  return Boolean(entry.sku?.trim());
}

/**
 * Pick a product and a quantity. Its own component so the catalogue is only loaded when somebody
 * is actually choosing — the index is the largest thing the panel reads — and so that its search
 * box and its quantity are thrown away with it when the choice is saved or abandoned.
 */
function GiveawayForm({
  initialQuantity,
  quantityMax,
  busy,
  onSave,
  onCancel,
}: {
  initialQuantity: number;
  quantityMax: number;
  busy: boolean;
  onSave: (variantId: string, quantity: number) => Promise<void>;
  onCancel: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const { loaded, prepared, settings } = useCatalogue();
  const [picked, setPicked] = useState<CatalogueEntry>();
  const [quantity, setQuantity] = useState<number | string>(initialQuantity);

  const whole = typeof quantity === "number" ? quantity : Number(quantity);
  const valid = Number.isInteger(whole) && whole >= 1 && whole <= quantityMax;
  const short =
    picked !== undefined && picked.available !== null && picked.available < whole;

  return (
    <Stack gap="xs">
      {!loaded ? (
        <Group gap="xs">
          <Loader size="xs" color="ink" />
          <Text size="xs" c="dimmed">
            {t("catalogue.loading")}
          </Text>
        </Group>
      ) : (
        <CatalogueSearch
          prepared={prepared}
          settings={settings}
          pickedId={picked?.variantId}
          onPick={setPicked}
          onClear={() => setPicked(undefined)}
          placeholder={t("giveaway.placeholder")}
          eligible={hasSku}
          /* The feature search owns `/` on this page. */
          shortcut={false}
        />
      )}

      {picked ? (
        <Text size="sm" fw={600}>
          {picked.productTitle}
          {picked.variantTitle ? ` — ${picked.variantTitle}` : ""}
        </Text>
      ) : null}

      <Group gap="xs" align="flex-end" wrap="wrap">
        <NumberInput
          label={t("giveaway.quantity")}
          description={t("giveaway.quantityHelp")}
          value={quantity}
          onChange={setQuantity}
          min={1}
          max={quantityMax}
          allowDecimal={false}
          allowNegative={false}
          w={200}
          size="sm"
        />
        <Button
          size="sm"
          disabled={!picked || !valid}
          loading={busy}
          onClick={() => {
            if (picked && valid) void onSave(picked.variantId, whole);
          }}
        >
          {t("giveaway.save")}
        </Button>
        {onCancel ? (
          <Button size="sm" variant="default" onClick={onCancel}>
            {t("giveaway.cancel")}
          </Button>
        ) : null}
      </Group>

      {short ? (
        <Text size="xs" c="orange.8">
          {t("giveaway.lowStock", { available: picked.available, quantity: whole })}
        </Text>
      ) : null}
    </Stack>
  );
}
